import React, { useEffect, useState } from "react"
import { HiOutlineMailOpen, HiOutlinePencilAlt, HiOutlineTrash } from "react-icons/hi"
import intl from "components/i18n/ReactIntlWrapper"
import { PermissionsGate } from "components/PermissionsGate"
import { useApi } from "api/ApiProvider"
import Api from "axiosApi/api"
import { handleAPIError } from "common/errorHandler"
import { useToastMessageQueue } from "components/ToastMessages/ToastMessageProvider"
import { ListOptionDTO, Persona } from "axiosApi/models"
import { Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material"
import { SCOPES } from "common/permissions"
import PersonaBillingModal from "./PersonaBillingModal"
import Spinner from "components/Spinner"
import { confirmAlert } from "react-confirm-alert"

export default function BillingTable({
    entityId,
    idEntityType,
    billToDisplay
}:
{
    entityId: number,
    idEntityType: number,
    billToDisplay?: boolean
}) {
  const [loading, setLoading] = useState(false)
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [editPersona, setEditPersona] = useState<Persona | null>(null)
  const [errors, setErrors] = useState({})
  const { invoiceApi }: Api = useApi()
  const toast = useToastMessageQueue()
  const [remainingPersonaTypes, setRemainingPersonaTypes] = useState<ListOptionDTO[]>([])

  const fetchBillingPersonas = async () => {
    setLoading(true);
    const { data: personaTypes } = await invoiceApi.apiVversionListPersonaTypeGet("1");
    const { data: personas } = await invoiceApi.apiVversionPersonaEntityIdEntityIdGet(entityId, "1", idEntityType, {});
    setPersonas(personas.data?.["queryResult"]);
    setRemainingPersonaTypes(personaTypes.data?.["queryResult"].filter((personaType: ListOptionDTO) => {
      return !personas.data?.["queryResult"].some((persona: Persona) => {
        return persona.idPersonaType === personaType.id
      })
    }))
    setLoading(false);
  }

  

  useEffect(() => {
    fetchBillingPersonas();
  }, [])

  const editScopes = [SCOPES['employees.edit']]
  const deleteScopes = [SCOPES['employees.edit']]

  const handleDelete = (id: number) => {
    confirmAlert({
        title: intl.get('delete.modal.title'),
        message: intl.get('delete.modal.personaRow.message'),
        buttons: [
            {
                label: intl.get('delete.modal.cancel.button'),
                onClick: () => { }
            },
            {
                label: intl.get('delete.modal.delete.button'),
                onClick: async () => {
                    try {
                      const response = await invoiceApi.apiVversionPersonaIdDelete(id, "1", {});
                      fetchBillingPersonas();
                      setEditPersona(null)
                      toast.success({ body: intl.get('personaRow.toast.success.handledelete')});
                    } catch (error) {
                      handleAPIError(error, toast, errors);
                      setErrors({...errors});
                    }
                },
            }
        ]
    });
  };

  return (
    <>
      <div className="card-header">
        <h4 className="title">
          <HiOutlineMailOpen className="mb-1" />{" "}
          {intl.get("personaCompact.header")}
        </h4>
      </div>
      {loading === true ? (
        <Spinner small={true} />
      ) : (
        <div className="card-body">
          <TableContainer >
              <Table>
                  <TableHead>
                      <TableRow>
                          <TableCell component="th" scope="col">{intl.get("personaCompact.table.thead.name")}</TableCell>
                          <TableCell component="th" scope="col">{intl.get("personaCompact.table.thead.type")}</TableCell>
                          <TableCell component="th" scope="col">{intl.get("personaCompact.table.thead.taxId")}</TableCell>
                          {billToDisplay &&
                            <TableCell component="th" scope="col">{intl.get("personaCompact.table.thead.billTo")}</TableCell>
                          }
                          <TableCell component="th" scope="col" className="text-center">{intl.get("personaCompact.table.thead.actions")}</TableCell>
                      </TableRow>
                  </TableHead>
                  <TableBody>
                    {personas?.map(persona=>{
                      return <TableRow key={persona?.id}>
                        <TableCell component="td" scope="row">{persona?.name}</TableCell>
                        <TableCell component="td" scope="row">{intl.get(persona?.personaType?.labelKey)}</TableCell>
                        <TableCell component="td" scope="row">{persona?.taxID}</TableCell>
                        {billToDisplay &&
                          <TableCell component="td" scope="row">
                            {persona?.invoicingEligibilities?.map((invoicingEligibility, index) =>
                              <Chip
                                key={index}
                                label={invoicingEligibility?.personaTo?.name}
                                variant="outlined"
                                size="small"
                                className="text-white bg-secondary me-1 border-white fw-semibold"
                              />
                            )}
                          </TableCell>
                        }
                        <TableCell component="td" scope="row" className='text-center'>
                          <PermissionsGate viewScopes={editScopes} editScopes={editScopes} viewRoles={[]} editRoles={[]} >
                            <a className={'td-icon mx-1'} onClick={()=>{
                              setEditPersona(persona)
                              setErrors({})
                            }} title={intl.get('personaRow.editButton.title')}>
                                <HiOutlinePencilAlt/>
                            </a>
                          </PermissionsGate>
                          <PermissionsGate viewScopes={deleteScopes} editScopes={deleteScopes} viewRoles={[]} editRoles={[]} > 
                            <a className={'td-icon mx-1'} onClick={()=>{handleDelete(persona?.id)}} title={intl.get('personaRow.deleteButton.title')}>
                              <HiOutlineTrash/>
                            </a>
                          </PermissionsGate>
                        </TableCell>
                      </TableRow>
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              <PersonaBillingModal
                entityId={entityId}
                idEntityType={idEntityType}
                editPersona={editPersona}
                onClose={() => {
                  setEditPersona(null)
                }}
                onSave={async () => {
                  await fetchBillingPersonas();
                  setEditPersona(null);
                  toast.success({ body: intl.get('persona.toast.success.handleSave')});
                }}
                editScopes={editScopes}
                deleteScopes={deleteScopes}
                remainingPersonaTypes={remainingPersonaTypes}
                billToDisplay={billToDisplay}
              />
        </div>
      )}
    </>
  )
}
